import React, { useState, useEffect, useRef } from "react";
import { injectIntl } from "react-intl";
import { Typography, Stack, IconButton, Box, Button } from "@mui/material";
import { inject, observer } from "mobx-react";
import { Icon } from '@iconify/react';
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import dayjs from "dayjs";
import { CalendarWrap, HomeContBox } from "../Styled/HomeStyled";
import { CalendarBox } from "@/common/Styled/CalendarStyles";
import SecretPokoro from "@/common/Images/SecretPokoro.svg";
import BaseDialog from "../../CommonComponent/BaseDialog";
import MobileDatePicker from "./MobileDatePicker";

const HomeCalendar = ({ 
  intl, 
  authStore, 
  userStore, 
  homeStore, 
  pronunciationStore, 
  reportStore, 
  languageStore 
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [showYearMonthPicker, setShowYearMonthPicker] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);
  const [isWeekView, setIsWeekView] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [viewDate, setViewDate] = useState(() => homeStore.selectedCalendarDate || new Date());
  const [selectedYear, setSelectedYear] = useState(() => (homeStore.selectedCalendarDate || new Date()).getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => (homeStore.selectedCalendarDate || new Date()).getMonth());
  const calendarRef = useRef();

  /* 캘린더 외부 클릭 시 닫힘 */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
        document.body.style.overflow = "auto";
      }
    };
    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto"; 
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [showCalendar]);

  /* 선택된 날짜가 변경될 때 뷰 날짜 동기화 */
  useEffect(() => {
    if (homeStore.selectedCalendarDate) {
      setViewDate(homeStore.selectedCalendarDate);
    }
  }, [homeStore.selectedCalendarDate]);

  /* 캘린더 데이터가 로드될 때마다 캘린더 강제 업데이트 */
  useEffect(() => {
    if (homeStore.calendarDateList && homeStore.calendarDateList.length > 0) {
      // 캘린더 컴포넌트를 강제로 다시 렌더링하기 위해 key 값 변경
      setCalendarKey((prevKey) => prevKey + 1);
    }
  }, [homeStore.calendarDateList]);

  /* 초기 로딩 시 현재 날짜의 홈 데이터 로드 */
  useEffect(() => {
    // 사용자 디바이스가 선택되고 캘린더 데이터가 있을 때 현재 날짜의 홈 데이터 로드
    if (userStore.selectedDevice && userStore.selectedDevice.deviceId && 
        homeStore.calendarDateList && !homeStore.homeData) {
      homeStore.getHomeData(
        authStore.loginUser.id,
        userStore.selectedDevice.deviceId
      );
    }
  }, [userStore.selectedDevice, homeStore.calendarDateList, authStore.loginUser.id, homeStore.homeData]);

  /* 월간 뷰 타이틀 날짜 표시 포맷 */
  const getMonthDisplay = () => {
    const year = dayjs(viewDate).format('YYYY');
    const month = dayjs(viewDate).format('MM');
  
    return intl.formatMessage(
      { id: "week_display" },
      { year, month }
    );
  };

  /* 월간 뷰 네비게이션 핸들러 (뷰 날짜만 변경) */
  const handleMonthArrowClick = (direction) => {
    const newViewDate = direction === 'previous' ? 
      dayjs(viewDate).subtract(1, 'month').toDate() : 
      dayjs(viewDate).add(1, 'month').toDate();

    setViewDate(newViewDate);
    
    // 캘린더 뷰 업데이트를 위해 activeStartDate 변경
    homeStore.setSelectedCalendarYear(newViewDate.getFullYear());
    homeStore.setSelectedCalendarMonth(newViewDate.getMonth() + 1);
    homeStore.setIsChangeCalendar(true);

    // 새로운 월의 캘린더 데이터 가져오기
    if (userStore.selectedDevice && userStore.selectedDevice.deviceId) {
      homeStore.getCalendar(
        authStore.loginUser.id,
        userStore.selectedDevice.deviceId
      );
    }
  };


  /* 주간/월간 뷰 전환 핸들러 */
  const handleToggleView = () => {
    const newIsWeekView = !isWeekView;
    
    // 트랜지션 시작
    setIsTransitioning(true);
    
    // 뷰가 변경될 때 날짜 조정
    let targetDate;

    if (newIsWeekView) {
      if (dayjs(homeStore.selectedCalendarDate).month() !== dayjs(viewDate).month()) {
        // 월간 -> 주간: 현재 월간뷰에서 보고 있는 달의 1일이 포함된 주를 표시∂∂
        const firstDayOfMonth = dayjs(viewDate).startOf('month');
        targetDate = firstDayOfMonth.toDate();
      } else {
        targetDate = dayjs(viewDate).date(dayjs(homeStore.selectedCalendarDate).date()).toDate();
      }
    } else {
      // 주간 -> 월간: 현재 viewDate(주간 뷰에서 보고 있던 날짜)를 기준으로 월 설정
      targetDate = viewDate;
      
      // 월간 뷰에서 해당 월의 캘린더 데이터를 가져오기 위해 store 업데이트
      homeStore.setSelectedCalendarYear(targetDate.getFullYear());
      homeStore.setSelectedCalendarMonth(targetDate.getMonth() + 1);
      homeStore.setIsChangeCalendar(true);
    }
    
    setViewDate(targetDate);
    
    // 트랜지션 효과를 위한 단일 지연 후 뷰 변경
    setTimeout(() => {
      setIsWeekView(newIsWeekView);
      setIsTransitioning(false);
    }, 200); // 단일 트랜지션으로 간소화
  };

  /* 주간 뷰에서 표시할 주의 시작과 끝 날짜 계산 */
  const getWeekRange = (date) => {
    const startOfWeek = dayjs(date).startOf('week');
    const endOfWeek = dayjs(date).endOf('week');
    return { startOfWeek, endOfWeek };
  };

  /* 주간 뷰에서 날짜 표시 포맷 - 월별로 분리된 주 기준 */
  const getWeekDisplay = () => {
    const year = dayjs(viewDate).format('YYYY');
    const month = dayjs(viewDate).format('MM');
    
    // viewDate가 속한 월만 표시 (월별로 주를 분리해서 보여주므로)
    return intl.formatMessage(
      { id: "week_display" },
      { year, month }
    );
  };

  /* 주간 뷰 네비게이션 핸들러 - 월별로 주를 분리해서 표시 */
  const handleWeekArrowClick = (direction) => {
    let newViewDate;
    
    if (direction === 'previous') {
      // 이전 주로 이동: 현재 월의 이전 주 또는 이전 월의 마지막 주
      const currentMonth = dayjs(viewDate).month();
      const currentYear = dayjs(viewDate).year();
      
      // 현재 날짜가 속한 월의 첫 번째 주인지 확인
      const firstDayOfMonth = dayjs(viewDate).startOf('month');
      const currentWeekStart = dayjs(viewDate).startOf('week');

      // 현재 주의 시작일이 이번 달 1일보다 이전이면 (즉, 현재 주가 이번 달 첫 주)
      if (currentWeekStart.isBefore(firstDayOfMonth, 'day')) {
        // 이전 월의 마지막 주로 이동 (이전 월의 마지막 날이 포함된 주)
        const lastDayOfPreviousMonth = firstDayOfMonth.subtract(1, 'day');
        newViewDate = lastDayOfPreviousMonth.toDate();
      } else {
        // 같은 월 내에서 이전 주로 이동
        if (dayjs(currentWeekStart).diff(firstDayOfMonth, 'day') < 7) {
          newViewDate = dayjs(firstDayOfMonth).toDate();
        } else {
          newViewDate = dayjs(viewDate).subtract(1, 'week').toDate();
        }
      }
    } else {
      // 다음 주로 이동: 현재 월의 다음 주 또는 다음 월의 첫 주
      const currentMonth = dayjs(viewDate).month();
      const currentYear = dayjs(viewDate).year();
      
      // 현재 날짜가 속한 월의 마지막 주인지 확인
      const lastDayOfMonth = dayjs(viewDate).endOf('month');
      const currentWeekEnd = dayjs(viewDate).endOf('week');

      // 현재 주의 끝일이 이번 달 마지막 일보다 이후이면 (즉, 현재 주가 이번 달 마지막 주)
      if (currentWeekEnd.isAfter(lastDayOfMonth, 'day')) {
        // 다음 월의 첫 주로 이동 (다음 월의 1일이 포함된 주)
        const firstDayOfNextMonth = lastDayOfMonth.add(1, 'day');
        newViewDate = firstDayOfNextMonth.toDate();
      } else {
        // 같은 월 내에서 다음 주로 이동
        newViewDate = dayjs(viewDate).add(1, 'week').toDate();
      }
    }

    setViewDate(newViewDate);
    
    // 주간 뷰에서도 월이 바뀔 수 있으므로 캘린더 뷰 업데이트
    const currentMonth = dayjs(viewDate).month();
    const newMonth = dayjs(newViewDate).month();
    
    if (currentMonth !== newMonth) {
      homeStore.setSelectedCalendarYear(newViewDate.getFullYear());
      homeStore.setSelectedCalendarMonth(newViewDate.getMonth() + 1);
      homeStore.setIsChangeCalendar(true);

      // 새로운 월의 캘린더 데이터 가져오기
      if (userStore.selectedDevice && userStore.selectedDevice.deviceId) {
        homeStore.getCalendar(
          authStore.loginUser.id,
          userStore.selectedDevice.deviceId
        );
      }
    }
  };


  // Function to display SecretPokoro character for dates with data
  const tileContent = ({ date, view }) => {
    if (view === "month" && homeStore.calendarDateList && homeStore.calendarDateList.length > 0) {
      const dateString = moment(date).format("YYYY-MM-DD");
      const hasData = homeStore.calendarDateList.includes(dateString);
      
      if (hasData) {
        return (
          <img
            src={SecretPokoro}
            alt="데이터 있는 날짜"
            style={{ width: "33px", height: "33px" }}
            className="pokoro-icon"
          />
        );
      }
    }
    return null;
  };

  /* 주간 뷰에서 타일 클래스명 결정 */
  const getTileClassName = ({ date, view }) => {
    let classNames = [];
    
    // 요일별 클래스 추가
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0) { // 일요일
      classNames.push('react-calendar__tile--sunday');
    } else if (dayOfWeek === 6) { // 토요일
      classNames.push('react-calendar__tile--saturday');
    }
    
    // 주간 뷰에서 숨김 처리
    if (view === "month" && isWeekView) {
      const { startOfWeek, endOfWeek } = getWeekRange(viewDate);
      
      // 현재 뷰 주에 포함되지 않는 날짜는 숨김
      if (dayjs(date).isBefore(startOfWeek, 'day') || dayjs(date).isAfter(endOfWeek, 'day')) {
        classNames.push('week-hidden');
      }
    }
    
    return classNames.length > 0 ? classNames.join(' ') : null;
  };

  const handleChangeActiveDate = (event) => {
    const activeStartDate = event.activeStartDate;
    
    homeStore.setSelectedCalendarYear(activeStartDate.getFullYear());
    homeStore.setSelectedCalendarMonth(activeStartDate.getMonth() + 1);
    homeStore.setIsChangeCalendar(true);

    if (userStore.selectedDevice && userStore.selectedDevice.deviceId) {
      homeStore.getCalendar(
        authStore.loginUser.id,
        userStore.selectedDevice.deviceId
      );
    }
  };

  /* 날짜 변경 핸들러 */
  const handleDateChange = (date) => {
    if (date) {
      setShowCalendar(false);
      document.body.style.overflow = "auto";

      homeStore.setSelectedCalendarDate(date);
      
      if (userStore.selectedDevice && userStore.selectedDevice.deviceId) {
        homeStore.getHomeData(
          authStore.loginUser.id,
          userStore.selectedDevice.deviceId
        );
      }

      pronunciationStore.setDate(dayjs(date));
      reportStore.setDate(dayjs(date));
    }
  };

  const handleOpenCalendar = () => {
    setShowYearMonthPicker(true);
    // 현재 날짜 기준으로 안전하게 설정
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    
    setSelectedYear(currentYear);
    setSelectedMonth(currentMonth);
  };

  /* 년월 선택 팝업에서 확인 버튼 클릭 핸들러 */
  const handleYearMonthConfirm = () => {
    const newDate = new Date(selectedYear, selectedMonth, 1);

    setViewDate(newDate);
    setShowYearMonthPicker(false);
    
    // 캘린더 뷰 업데이트를 위해 store 업데이트
    homeStore.setSelectedCalendarYear(selectedYear);
    homeStore.setSelectedCalendarMonth(selectedMonth + 1);
    homeStore.setIsChangeCalendar(true);

    // 새로운 월의 캘린더 데이터 가져오기
    if (userStore.selectedDevice && userStore.selectedDevice.deviceId) {
      homeStore.getCalendar(
        authStore.loginUser.id,
        userStore.selectedDevice.deviceId
      );
    }
  };

  /* 년월 선택 팝업에서 취소 버튼 클릭 핸들러 */
  const handleYearMonthCancel = () => {
    setShowYearMonthPicker(false);
    setSelectedYear(viewDate.getFullYear());
    setSelectedMonth(viewDate.getMonth());
  };


  // 팝업 캘린더용 데이터 있는 날짜 표시
  const popupTileContent = ({ date }) => {
    if (homeStore.calendarDateList.includes(moment(date).format("YYYY-MM-DD"))) {
      return (
          <span
              aria-label="데이터 있는 날짜 표시"
              style={{
                width: "4px",
                height: "4px",
                background: "#FF993C",
                borderRadius: "100px",
                position: "absolute",
                bottom: 6
              }}
          />
      );
    }
    return null;
  };


  return (
    <CalendarWrap>
      <Typography variant="h6" fontWeight={700}>
        {intl.formatMessage({ id: "pokoro_learning_status" })} {/* 포코로 학습현황 */}
      </Typography>
      
      {/* 년월 선택 팝업 */}
      <BaseDialog
        open={showYearMonthPicker}
        title={intl.formatMessage({ id: "date_selection" })} /* 날짜 선택 */
        actionType="double"
        leftButtonText={intl.formatMessage({ id: "dialog_cancle" })} /* 취소 */
        rightButtonText={intl.formatMessage({ id: "dialog_confirmButton" })} /* 확인 */
        onLeftClick={handleYearMonthCancel}
        onRightClick={handleYearMonthConfirm}
      >
        <MobileDatePicker
          selectedYear={selectedYear}
          selectedMonth={selectedMonth + 1}
          onYearChange={setSelectedYear}
          onMonthChange={(month) => setSelectedMonth(month - 1)}
          maxYear={new Date().getFullYear()}
        />
      </BaseDialog>
      
      <HomeContBox py={1}>
        <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
          <IconButton 
            size="large" 
            onClick={() => isWeekView ? handleWeekArrowClick('previous') : handleMonthArrowClick('previous')}
          >
            <Icon icon={'iconamoon:arrow-left-2-thin'} />
          </IconButton>
          <span onClick={handleOpenCalendar} style={{ cursor: 'pointer', fontSize: '16px', fontWeight: 500 }}>
            {isWeekView ? getWeekDisplay() : getMonthDisplay()}
          </span>
          <IconButton 
            size="large" 
            onClick={() => isWeekView ? handleWeekArrowClick('next') : handleMonthArrowClick('next')}
          >
            <Icon icon={'iconamoon:arrow-right-2-thin'} />
          </IconButton>
        </Stack>
        <CalendarBox className={`home ${isWeekView ? 'week-view' : ''} ${isTransitioning ? 'transitioning' : ''}`}>
          <Calendar
            key={calendarKey}
            locale={languageStore.language}
            activeStartDate={viewDate}
            onActiveStartDateChange={handleChangeActiveDate}
            onChange={handleDateChange}
            value={homeStore.selectedCalendarDate}
            defaultValue={homeStore.selectedCalendarDate}
            tileContent={tileContent}
            tileClassName={getTileClassName}
            formatDay={(locale, date) => moment(date).format("D")}
            calendarType="gregory"
            showNeighboringMonth={false}
            showNavigation={false}
            tileDisabled={() => false}
            prev2Label={null}
            next2Label={null}
          />
            <IconButton 
              size="small" 
              onClick={handleToggleView}
              style={{ padding: '2px' }}
            >
              <Icon 
                icon={isWeekView ? 'iconamoon:arrow-down-2-thin' : 'iconamoon:arrow-up-2-thin'} 
                style={{ fontSize: '16px' }}
              />
            </IconButton>
        </CalendarBox>
      </HomeContBox>
    </CalendarWrap>
  );
};

export default injectIntl(
  inject(
    "authStore",
    "userStore", 
    "homeStore", 
    "pronunciationStore", 
    "reportStore", 
    "languageStore"
  )(observer(HomeCalendar))
);
